import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import './App.css';
import Header from './Components/Header';
import HeroSection from './Components/HeroSection';
import FileUploadForm from './Components/FileUploadForm';
import ResultsSection from './Components/ResultsSection';
import ParticleBackground from './Components/ParticleBackground';
import ProgressTracker from './Components/ProgressTracker';
import { AuthProvider } from './Components/AuthContext';
import LoginPage from './Components/LoginPage';
import ProtectedRoute from './Components/ProtectedRoute';
import OAuthCallback from './Components/OAuthCallback';

const HOSTNAME = process.env.REACT_APP_HOSTNAME

// Particle configuration - defined once outside component
const particleConfig = {
  particleCount: 80,
  colors: ['#6366f1', '#8a5cf6', '#a78bfa'],
  interactivity: true,
};

function App() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [download_url, setDownloadURL] = useState(null)
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    // Reset states
    setResult(null);
    setError(null);
    
    // Check if file is a Word document
    if (selectedFile && selectedFile.name.endsWith('.docx')) {
      setFile(selectedFile);
    } else if (selectedFile) {
      setError('Please upload a Word (.docx) document');
      setFile(null);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear email error when user starts typing
    if (emailError) {
      setEmailError('');
    }
    
    // Basic email validation if email is provided
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file first');
      return;
    }

    // Validate email if provided
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setIsProcessing(true);
    
    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    
    // Add email if provided
    if (email) {
      formData.append('email', email);
    }
    
    try {
      // Send file to backend
      const response = await fetch(`${HOSTNAME}/api/upload`, {
        method: 'POST',
        body: formData,
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Something went wrong');
      }
      
      if (!data.success) {
        throw new Error(data.error || 'Processing failed');
      }
      
      setResult(data);
      setDownloadURL(data.download_url)
      
      // Debug: Log the entire response data
      console.log('Response data:', data);
      console.log('Email sent field:', data.email_sent);
      
      // Show email notification status if email was provided
      if (email && data.email_sent !== undefined) {
        if (data.email_sent) {
          // You could add a success notification here
          console.log('Email notification sent successfully');
          alert('✅ Email notification sent successfully to ' + email);
        } else {
          console.warn('Email notification failed to send');
          alert('⚠️ Email notification failed to send. Please check your email address.');
        }
      }
    } catch (err) {
      setError(err.message);
      console.log('This is the error ' + err.message);
    } finally {
      setLoading(false);
      setIsProcessing(false);
    }

  };

  const handle_download = (e) => {

    if(!download_url){
      alert('No file ready for download yet');
      return;
    }
    window.location.href = `${HOSTNAME}${download_url}`;
  }

  const ProtectedAppContent = (
    <div className="min-h-screen bg-[#0a0b14] text-white relative">
      <ParticleBackground config={particleConfig} />
      <div className="relative z-10">
        <Header />
        <main className="pt-8 pb-20">
          <HeroSection handleFileChange={handleFileChange} />
          <FileUploadForm
            file={file}
            handleFileChange={handleFileChange}
            handleSubmit={handleSubmit}
            loading={loading}
            error={error}
            email={email}
            handleEmailChange={handleEmailChange}
            emailError={emailError}
          />
          <ProgressTracker isProcessing={isProcessing} />
          {download_url && (
            <div className="flex justify-center mt-4">
              <button
                onClick={handle_download}
                className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white text-lg font-semibold rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Click here to download the processed document
              </button>
            </div>
          )}
          {result && <ResultsSection result={result} />}
        </main>
        <footer className="bg-gray-900/30 backdrop-blur-sm py-6 border-t border-gray-800/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} Legal Citation Generator. In collaboration with Bonsai ACG
            </div>
          </div>
        </footer>
      </div>
    </div>
  );

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/" element={<ProtectedRoute>{ProtectedAppContent}</ProtectedRoute>} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;